import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { TriangleAlert, X } from "lucide-react";
import type { CSSProperties, FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import type { ButtonProps } from "./button";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

const BACKDROP_CLASS =
  "fixed inset-0 z-[1000] bg-noa-overlay transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0";

const POPUP_CLASS =
  "relative flex w-full max-w-[calc(100vw-2rem)] flex-col rounded-lg bg-noa-elevated px-6 py-5 text-sm text-noa shadow-xl shadow-noa-shadow outline-none transition-[opacity,scale] duration-200 data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0";

const TITLE_CLASS = "m-0 text-base leading-normal font-semibold text-noa-strong";

const toCssSize = (size: number | string) => (typeof size === "number" ? `${size}px` : size);

export interface DialogProps {
  open: boolean;
  /** Called on the X, Esc, backdrop click and the default Cancel button. */
  onCancel?: () => void;
  /** Called by the default OK button. */
  onOk?: () => void;
  title?: ReactNode;
  /** Accessible name when there is no visible `title`. */
  "aria-label"?: string;
  children?: ReactNode;
  /** `null` hides the footer; omitted renders Cancel + OK. */
  footer?: ReactNode | null;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  /** Spinner on the default OK button. */
  confirmLoading?: boolean;
  /** Popup width, default 520 (px when a number). Always capped at the viewport minus 2rem. */
  width?: number | string;
  /** Vertically centered; otherwise 100px from the top (antd's default). */
  centered?: boolean;
  /** Show the X close button (default true). */
  closable?: boolean;
  /** Close on backdrop click (default true). */
  maskClosable?: boolean;
  /** Keep the content mounted while closed. By default it unmounts on close — antd's `destroyOnHidden`. */
  keepMounted?: boolean;
  /** Called once the close animation has finished. */
  afterClose?: () => void;
  /** Applied to the popup (the antd `.ant-modal-container`). */
  className?: string;
  style?: CSSProperties;
  classNames?: { header?: string; body?: string; footer?: string };
}

/** Modal dialog (antd `Modal` replacement) on Base UI Dialog. */
export const Dialog: FunctionComponent<DialogProps> = ({
  open,
  onCancel,
  onOk,
  title,
  "aria-label": ariaLabel,
  children,
  footer,
  okText,
  cancelText,
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
  width = 520,
  centered,
  closable = true,
  maskClosable = true,
  keepMounted,
  afterClose,
  className,
  style,
  classNames,
}) => {
  const { t } = useTranslation();

  const defaultFooter = (
    <>
      <Button
        {...cancelButtonProps}
        onClick={(event) => {
          cancelButtonProps?.onClick?.(event);
          onCancel?.();
        }}
      >
        {cancelText ?? t("common:cancel")}
      </Button>
      <Button
        variant="primary"
        loading={confirmLoading}
        {...okButtonProps}
        onClick={(event) => {
          okButtonProps?.onClick?.(event);
          onOk?.();
        }}
      >
        {okText ?? t("common:ok")}
      </Button>
    </>
  );

  return (
    <BaseDialog.Root
      open={open}
      // While the OK action runs, Esc / backdrop / X must not dismiss the dialog out from under it.
      onOpenChange={(nextOpen) => !nextOpen && !confirmLoading && onCancel?.()}
      onOpenChangeComplete={(isOpen) => !isOpen && afterClose?.()}
      disablePointerDismissal={!maskClosable}
    >
      <BaseDialog.Portal keepMounted={keepMounted}>
        <BaseDialog.Backdrop className={BACKDROP_CLASS} />
        <BaseDialog.Viewport
          className={cn(
            "fixed inset-0 z-[1000] flex justify-center overflow-y-auto px-4",
            centered ? "items-center py-6" : "items-start pt-[100px] pb-6"
          )}
        >
          <BaseDialog.Popup
            aria-label={title ? undefined : ariaLabel}
            className={cn(POPUP_CLASS, className)}
            style={{ width: toCssSize(width), ...style }}
          >
            {closable && (
              <BaseDialog.Close
                aria-label={t("common:close")}
                className={cn(
                  "absolute top-4 right-4 inline-flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-noa-muted transition-colors hover:bg-noa-hover hover:text-noa",
                  FOCUS_RING
                )}
              >
                <X className="size-4" />
              </BaseDialog.Close>
            )}
            {title && (
              <div className={cn("mb-2 pr-8", classNames?.header)}>
                <BaseDialog.Title className={TITLE_CLASS}>{title}</BaseDialog.Title>
              </div>
            )}
            <div className={cn("min-h-0", classNames?.body)}>{children}</div>
            {footer !== null && (
              <div className={cn("mt-3 flex justify-end gap-2", classNames?.footer)}>
                {footer === undefined ? defaultFooter : footer}
              </div>
            )}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
};

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  content?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  onOk: () => void;
  onCancel: () => void;
  /** Destructive confirm (antd `okType: "danger"`). */
  danger?: boolean;
  confirmLoading?: boolean;
}

/** Controlled confirmation (antd `Modal.confirm` replacement) on Base UI AlertDialog. */
export const ConfirmDialog: FunctionComponent<ConfirmDialogProps> = ({
  open,
  title,
  content,
  okText,
  cancelText,
  onOk,
  onCancel,
  danger,
  confirmLoading,
}) => {
  const { t } = useTranslation();

  return (
    <AlertDialog.Root
      open={open}
      // Same guard as Dialog: while the confirmed action runs, Esc must not dismiss the dialog.
      onOpenChange={(nextOpen) => !nextOpen && !confirmLoading && onCancel()}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={BACKDROP_CLASS} />
        <AlertDialog.Viewport className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto px-4 pt-[100px] pb-6">
          <AlertDialog.Popup className={cn(POPUP_CLASS, "w-[416px] px-8 py-6")}>
            <div className="flex gap-3">
              <TriangleAlert
                className={cn("mt-0.5 size-5.5 shrink-0", danger ? "text-noa-danger" : "text-noa-warning")}
              />
              <div className="min-w-0 flex-1">
                <AlertDialog.Title className={TITLE_CLASS}>{title}</AlertDialog.Title>
                {content && <AlertDialog.Description className="mt-2 mb-0">{content}</AlertDialog.Description>}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button onClick={onCancel} disabled={confirmLoading}>
                {cancelText ?? t("common:cancel")}
              </Button>
              <Button variant="primary" danger={danger} loading={confirmLoading} onClick={onOk}>
                {okText ?? t("common:ok")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};
